import { useState, useEffect } from 'preact/hooks';
import { Chapter } from '@novelizing/types';

// Get API_BASE from environment variable REACTION_API_URL
const getApiBase = (): string => {
  const apiBase =
    import.meta.env.VITE_REACTION_API_URL || 'https://reactions.novelizing.com';
  if (!apiBase) {
    throw new Error('VITE_REACTION_API_URL environment variable is not set');
  }
  return apiBase;
};

interface UseCommentCountProps {
  chapter: Chapter;
}

interface CommentCountResponse {
  count: number;
}

export const useCommentCount = ({ chapter }: UseCommentCountProps) => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch comment count for the entity
  const fetchCommentCount = async () => {
    if (!chapter.id) return;

    setLoading(true);
    setError(null);

    try {
      // Get API_BASE from environment variable
      const apiBase = getApiBase();
      const response = await fetch(
        `${apiBase}/comments/count/chapter/${chapter.id}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch comment count: ${response.status}`);
      }

      const data: CommentCountResponse = await response.json();
      setCount(data.count);
    } catch (err) {
      console.error('Error fetching comment count:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch comment count on mount and when entityId changes
  useEffect(() => {
    if (chapter.id) {
      fetchCommentCount();
    }
  }, [chapter.id]);

  return {
    count,
    loading,
    error,
    refreshCount: fetchCommentCount,
  };
};
